import { type DirectiveBinding, type ObjectDirective } from "@nuxtjs/composition-api";
import Vue from "vue";

declare module "vue" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ComponentOptions<V> {
    _componentTag?: string;
  }

  interface GlobalDirectives {
    vTestID: typeof testIdDirective;
  }
}

declare module "vue/types/vue" {
  interface Vue {
    layoutName: string;
  }
}

function slugify(str?: string) {
  return str ? (str.charAt(0).toLowerCase() + str.slice(1)).replace(/[A-Z]/g, (n) => "-" + n.toLowerCase()) : "";
}

function prepareTestId(name?: string, value?: string) {
  return `${name ? `${name}/` : ""}${slugify(value)}`;
}

// https://vuejs.org/guide/scaling-up/ssr.html#custom-directives
// https://vuejs.org/guide/reusability/custom-directives.html#directive-hooks
export const testIdDirective = <ObjectDirective<HTMLElement, string | Array<string> | undefined>>{
  bind(el, binding, vnode) {
    // client-side implementation:
    // directly update the DOM
    const _val = binding.value;
    const _options = vnode.context?.$options;
    const _layoutName = vnode.context?.$parent?.layoutName;
    const _att = el.getAttribute("data-test-id");

    const _name =
      // here we test to see if the component name is a single character,
      // which happens to some components during the build process,
      // and name it the correct layout
      (/^.$/g.test(_options?.name ?? "")
        ? // if a single character, and a layout name exists,
          // the directive was called on a root component
          _layoutName
          ? _layoutName + "-layout"
          : // otherwise we need to find the correct name,
            // since its only a single character
            _options?.__name || _options?._componentTag
        : // normal case, just use the name
          _options?.__name || _options?.name) ?? ""; // account for no name;
    // turn name into a slug
    const _slug = slugify(_name);

    // if the binding changes we want to update the attribute
    _val !== binding.oldValue &&
      el.setAttribute(
        "data-test-id",
        // if _att isn't falsy
        // add a space and print so we don't overwrite values
        (_att ? `${_att} ` : "") +
          // check for array, if so, map and join with a space
          (Array.isArray(_val)
            ? _val.map((v) => prepareTestId(_slug, v)).join(" ")
            : // otherwise just slugify the value
              prepareTestId(_slug, _val)),
      );
  },

  // can remove type declartion when we update to nuxt/vue 3
  getSSRProps(binding: DirectiveBinding<string | Array<string> | undefined>) {
    // server-side implementation:
    // return the props to be rendered.
    // getSSRProps only receives the directive binding.
    const val = binding.value;
    return {
      "data-test-id": Array.isArray(val) ? val.map((i) => i).join(" ") : val,
    };
  },
};

Vue.directive("test-id", testIdDirective);
